<script setup lang="ts">
import { useDebounceFn } from "@vueuse/core";
import { computed, ref, watch } from "vue";
import { getLegacyApp } from "../../core/legacy-bridge";
import { seriesLibrary } from "../../data";
import { t } from "../../i18n";
import { appStore } from "../../stores/app";
import { Preset, Series } from "../../types/type_def";
import TileList, { Tile, TileHeight } from "../common/TileList.vue";

/** Step 1: choose a series. Step 2: start from scratch ("Configure") or a preset. */
const step = ref<"series" | "preset">("series");
const search = ref("");
const series = ref<Series[]>([]);
const presets = ref<Preset[]>([]);

// Tile heights repeat in this pattern, which gives the masonry its staggered look
const heightPattern: TileHeight[] = ["large", "small", "medium", "medium", "large", "small"];
const heightAt = (i: number) => heightPattern[i % heightPattern.length];

const seriesTiles = computed<Tile[]>(() => series.value.map((s, i) => ({ key: s.id, name: s.name, thumbnail: s.thumbnail, height: heightAt(i) })));

// "Configure" comes first and shares the first height with the first preset (as before)
const presetTiles = computed<Tile[]>(() => [
    { key: "new", name: "Configure", height: heightAt(0), class: "is-new" },
    ...presets.value.map((p, i) => ({ key: p.id, name: p.name, thumbnail: p.thumbnail, height: heightAt(i) })),
]);

async function loadSeries() {
    series.value = await seriesLibrary.fetchSeries({ search: search.value });
}

// Every time the page is shown: back to step 1 with a fresh series list
watch(() => appStore.pageVisits, () => {
    if (appStore.page !== "series") return;
    step.value = "series";
    loadSeries();
});

const onSearchInput = useDebounceFn(() => loadSeries(), 250);

function clearSearch() {
    search.value = "";
    loadSeries();
}

async function selectSeries(tile: Tile) {
    const selected = await getLegacyApp().selectSeries(Number(tile.key));
    presets.value = selected.presets;
    step.value = "preset";
}

function selectPreset(tile: Tile) {
    // Only "Configure" does something; choosing a preset is not implemented
    if (tile.key === "new") {
        getLegacyApp().startConfiguring();
    }
}
</script>

<!-- Shown when appStore.page is "series" (set by showPage in app.ts) -->
<template>
    <div class="series-page page bulma-contextual" data-page="series" :class="{ 'is-shown': appStore.page === 'series' }">
        <div class="page__content columns is-gapless is-centered">
            <div class="column is-10">
                <section class="section">
                    <div class="series-selection wizard-nav">
                        <div class="wizard-nav__stepviews">

                            <div class="wizard-nav__stepview" :class="{ 'is-shown': step === 'series' }" data-stepview="series">
                                <div class="level">
                                    <div class="level-left">
                                        <div class="seriessearch control has-icons-left is-flex-shrink-3 mr-5">
                                            <form @submit.prevent @reset.prevent="clearSearch">
                                                <input id="seriessearch" v-model="search" name="seriessearch" required type="text" class="simple-input input is-shadowless is-radiusless" :placeholder="t('common.search')" @keyup="onSearchInput" />
                                                <span class="icon is-small is-left">
                                                    <i class="fas fa-search"></i>
                                                </span>
                                                <button class="icon close-icon is-right" type="reset"></button>
                                            </form>
                                        </div>
                                    </div>
                                </div>

                                <div class="level">
                                    <div class="level-left">
                                        <div class="level-item">
                                            <label class="wizard-nav__stepview__label is-uppercase">Choose sofa series</label>
                                        </div>
                                    </div>
                                    <div class="level-right">
                                        <div class="level-item">
                                            <ul class="series-categories list-filter"></ul>
                                        </div>
                                    </div>
                                </div>

                                <TileList class="series-list tile-list" :tiles="seriesTiles" @select="selectSeries" />
                            </div>

                            <div class="wizard-nav__stepview" :class="{ 'is-shown': step === 'preset' }" data-stepview="preset">
                                <div class="level">
                                    <div class="level-left">
                                        <div class="level-item">
                                            <label class="wizard-nav__stepview__label is-uppercase">START FROM SCRATCH OR SELECT A PRESET</label>
                                        </div>
                                    </div>
                                </div>

                                <!-- Only rendered once the series is chosen, so "Configure" can't run before its configuration exists -->
                                <TileList class="preset-list tile-list" :tiles="step === 'preset' ? presetTiles : []" @select="selectPreset" />
                            </div>

                        </div>
                    </div>
                </section>

            </div>
        </div>
    </div>
</template>
